import type {
  Gcms_ButtonAction,
  Gcms_ButtonTheme,
  Gcms_ButtonVariants,
  Gcms_ContentPosition,
  Gcms_FormInputType,
  Gcms_GridComponent,
  Gcms_GridLayout,
  Gcms_ParentCategory,
  Gcms_PostType,
  Gcms_PressItem,
  Gcms_PressItemType,
  Gcms_SocialPlatform,
  Gcms_SubcategoryType,
  Gcms_ThemeColor,
} from "lib/cms-typings";

export enum ButtonAction {
  Auth = "AUTH",
  CloseModal = "CLOSE_MODAL",
  ExternalLink = "EXTERNAL_LINK",
  Link = "LINK",
  OpenModal = "OPEN_MODAL",
  OpenSearch = "OPEN_SEARCH",
  PageLink = "PAGE_LINK",
  Scroll = "SCROLL",
  Submit = "SUBMIT",
  UseLocation = "USE_LOCATION",
}

export enum ButtonTheme {
  Amber = "amber",
  BlackAlpha = "blackAlpha",
  Blue = "blue",
  BlueGray = "blueGray",
  CoolGray = "coolGray",
  Cyan = "cyan",
  DarkGreen = "darkGreen",
  Emerald = "emerald",
  Fuchsia = "fuchsia",
  Gray = "gray",
  Green = "green",
  Indigo = "indigo",
  LightBlue = "lightBlue",
  Lime = "lime",
  Orange = "orange",
  Pink = "pink",
  PodcastBlue = "podcastBlue",
  Purple = "purple",
  Red = "red",
  Rose = "rose",
  Teal = "teal",
  Violet = "violet",
  WarmGray = "warmGray",
  WhiteAlpha = "whiteAlpha",
  Yellow = "yellow",
}

export enum FormInputType {
  Email = "EMAIL",
  Tel = "TEL",
  Text = "TEXT",
}

export enum ButtonVariants {
  Ghost = "ghost",
  Link = "link",
  Outline = "outline",
  Solid = "solid",
}

export enum PostType {
  KnowledgeCenter = "knowledge_center",
  Legal = "legal",
  PressRelease = "press_release",
  PressReport = "press_report",
}

export type GcmsAdvertisingListFragment = {
  __typename?: "GCMS_AdvertisingList";
  id: string;
  listItem: Array<string>;
};

export type GcmsAggregatePostPageFragment = {
  __typename?: "GCMS_AggregatePostPage";
  aggregateType: Gcms_PostType;
  title?: string | null;
  content?: { __typename?: "GCMS_RichText"; markdown: string } | null;
  posts: Array<{
    __typename?: "GCMS_Post";
    title: string;
    slug: string;
    createdAt: any;
    updatedAt: any;
    published: any;
    excerpt?: string | null;
    readingTime: number;
    postType: Gcms_PostType;
    trending?: boolean | null;
    author?: {
      __typename?: "GCMS_Author";
      slug: string;
      person?: {
        __typename?: "GCMS_Person";
        name: string;
        photo?: {
          __typename?: "GCMS_Asset";
          id: string;
          url: string;
          width?: number | null;
          height?: number | null;
          handle: string;
        } | null;
      } | null;
    } | null;
    coverImage?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
    aggregatePostPage?: {
      __typename?: "GCMS_AggregatePostPage";
      title?: string | null;
      slug: string;
    } | null;
  }>;
  seo?: {
    __typename?: "GCMS_Seo";
    description?: string | null;
    title?: string | null;
    keywords: Array<string>;
    image?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
  } | null;
};

export type GcmsAuthorFragment = {
  __typename?: "GCMS_Author";
  biography?: string | null;
  location?: string | null;
  slug: string;
  socialLinks: Array<{
    __typename?: "GCMS_SocialLink";
    platform: Gcms_SocialPlatform;
    url: string;
  }>;
  posts: Array<{
    __typename?: "GCMS_Post";
    title: string;
    slug: string;
    createdAt: any;
    updatedAt: any;
    published: any;
    excerpt?: string | null;
    readingTime: number;
    postType: Gcms_PostType;
    trending?: boolean | null;
    author?: {
      __typename?: "GCMS_Author";
      slug: string;
      person?: {
        __typename?: "GCMS_Person";
        name: string;
        photo?: {
          __typename?: "GCMS_Asset";
          id: string;
          url: string;
          width?: number | null;
          height?: number | null;
          handle: string;
        } | null;
      } | null;
    } | null;
    coverImage?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
    aggregatePostPage?: {
      __typename?: "GCMS_AggregatePostPage";
      title?: string | null;
      slug: string;
    } | null;
  }>;
  person?: {
    __typename?: "GCMS_Person";
    name: string;
    photo?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
  } | null;
};

export type GcmsAuthorPreviewFragment = {
  __typename?: "GCMS_Author";
  slug: string;
  person?: {
    __typename?: "GCMS_Person";
    name: string;
    photo?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
  } | null;
  posts: Array<{
    __typename?: "GCMS_Post";
    title: string;
    slug: string;
    createdAt: any;
    updatedAt: any;
    published: any;
    excerpt?: string | null;
    readingTime: number;
    postType: Gcms_PostType;
    trending?: boolean | null;
    coverImage?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
    aggregatePostPage?: {
      __typename?: "GCMS_AggregatePostPage";
      title?: string | null;
      slug: string;
    } | null;
  }>;
};

export type GcmsAuthorPreviewNonCyclicalFragment = {
  __typename?: "GCMS_Author";
  slug: string;
  person?: {
    __typename?: "GCMS_Person";
    name: string;
    photo?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
  } | null;
};

export type GcmsBannerFragment = {
  __typename?: "GCMS_Banner";
  id: string;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  message: { __typename?: "GCMS_RichText"; markdown: string };
  button?: {
    __typename?: "GCMS_Button";
    id: string;
    href?: string | null;
    label: string;
    theme?: Gcms_ButtonTheme | null;
    buttonAction: Gcms_ButtonAction;
    variant: Gcms_ButtonVariants;
    linkedPage?: { __typename?: "GCMS_Page"; id: string; slug: string } | null;
  } | null;
};

export type GcmsBlockquoteComponentFragment = {
  __typename?: "GCMS_BlockquoteComponent";
  id: string;
  description?: { __typename?: "GCMS_RichText"; raw: any } | null;
};

export type GcmsBreakpointFragment = {
  __typename: "GCMS_Breakpoint";
  id: string;
  title?: string | null;
  highlight?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  content?: {
    __typename?: "GCMS_BreakpointContentRichText";
    markdown: string;
  } | null;
  buttons: Array<{
    __typename?: "GCMS_Button";
    id: string;
    href?: string | null;
    label: string;
    theme?: Gcms_ButtonTheme | null;
    buttonAction: Gcms_ButtonAction;
    variant: Gcms_ButtonVariants;
    linkedPage?: { __typename?: "GCMS_Page"; id: string; slug: string } | null;
  }>;
};

export type GcmsButtonFragment = {
  __typename?: "GCMS_Button";
  id: string;
  href?: string | null;
  label: string;
  theme?: Gcms_ButtonTheme | null;
  buttonAction: Gcms_ButtonAction;
  variant: Gcms_ButtonVariants;
  linkedPage?: { __typename?: "GCMS_Page"; id: string; slug: string } | null;
};

export type GcmsCategoryFragment = {
  __typename?: "GCMS_Category";
  id: string;
  key: Gcms_ParentCategory;
  displayName: string;
  descriptionMobile: string;
  description: string;
  statCardBg?: Gcms_ThemeColor | null;
  knowledgeBaseSlug: string;
  subcategories: Array<{
    __typename?: "GCMS_Subcategory";
    id: string;
    key: Gcms_SubcategoryType;
    displayName?: string | null;
  }>;
};

export type GcmsCategoryPostPreviewsFragment = {
  __typename?: "GCMS_Category";
  knowledgeBaseSlug: string;
  post: Array<{
    __typename?: "GCMS_Post";
    postType: Gcms_PostType;
    slug: string;
  }>;
};

export type GcmsCategoryPostsFragment = {
  __typename?: "GCMS_Category";
  description: string;
  descriptionMobile: string;
  displayName: string;
  post: Array<{
    __typename?: "GCMS_Post";
    title: string;
    slug: string;
    createdAt: any;
    updatedAt: any;
    published: any;
    excerpt?: string | null;
    readingTime: number;
    postType: Gcms_PostType;
    trending?: boolean | null;
    author?: {
      __typename?: "GCMS_Author";
      slug: string;
      person?: {
        __typename?: "GCMS_Person";
        name: string;
        photo?: {
          __typename?: "GCMS_Asset";
          id: string;
          url: string;
          width?: number | null;
          height?: number | null;
          handle: string;
        } | null;
      } | null;
    } | null;
    coverImage?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
    aggregatePostPage?: {
      __typename?: "GCMS_AggregatePostPage";
      title?: string | null;
      slug: string;
    } | null;
  }>;
};

export type GcmsDatasetTeaserFragment = {
  __typename: "GCMS_DatasetTeaser";
  title?: string | null;
  subtitle?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  categories: Array<{
    __typename?: "GCMS_Category";
    id: string;
    key: Gcms_ParentCategory;
    displayName: string;
    descriptionMobile: string;
    description: string;
    statCardBg?: Gcms_ThemeColor | null;
    knowledgeBaseSlug: string;
    subcategories: Array<{
      __typename?: "GCMS_Subcategory";
      id: string;
      key: Gcms_SubcategoryType;
      displayName?: string | null;
    }>;
  }>;
};

export type GcmsDynamicPageFragment = {
  __typename?: "GCMS_Page";
  id: string;
  title: string;
  subtitle?: string | null;
  hero?: {
    __typename?: "GCMS_Hero";
    slug?: string | null;
    bg: Gcms_ThemeColor;
    color: Gcms_ThemeColor;
    title?: string | null;
    subtitle?: string | null;
    pushText?: string | null;
    image?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
    mobileImage?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
    buttons: Array<{
      __typename?: "GCMS_Button";
      id: string;
      href?: string | null;
      label: string;
      theme?: Gcms_ButtonTheme | null;
      buttonAction: Gcms_ButtonAction;
      variant: Gcms_ButtonVariants;
      linkedPage?: {
        __typename?: "GCMS_Page";
        id: string;
        slug: string;
      } | null;
    }>;
  } | null;
  blocks: Array<
    | {
        __typename: "GCMS_Breakpoint";
        id: string;
        title?: string | null;
        highlight?: string | null;
        bg: Gcms_ThemeColor;
        color: Gcms_ThemeColor;
        content?: {
          __typename?: "GCMS_BreakpointContentRichText";
          markdown: string;
        } | null;
        buttons: Array<{
          __typename?: "GCMS_Button";
          id: string;
          href?: string | null;
          label: string;
          theme?: Gcms_ButtonTheme | null;
          buttonAction: Gcms_ButtonAction;
          variant: Gcms_ButtonVariants;
          linkedPage?: {
            __typename?: "GCMS_Page";
            id: string;
            slug: string;
          } | null;
        }>;
      }
    | {
        __typename: "GCMS_DatasetTeaser";
        title?: string | null;
        subtitle?: string | null;
        bg: Gcms_ThemeColor;
        color: Gcms_ThemeColor;
        categories: Array<{
          __typename?: "GCMS_Category";
          id: string;
          key: Gcms_ParentCategory;
          displayName: string;
          descriptionMobile: string;
          description: string;
          statCardBg?: Gcms_ThemeColor | null;
          knowledgeBaseSlug: string;
          subcategories: Array<{
            __typename?: "GCMS_Subcategory";
            id: string;
            key: Gcms_SubcategoryType;
            displayName?: string | null;
          }>;
        }>;
      }
    | {
        __typename: "GCMS_FaqSection";
        id: string;
        bg: Gcms_ThemeColor;
        color: Gcms_ThemeColor;
        header?: string | null;
        faqs: Array<{
          __typename: "GCMS_Faq";
          id: string;
          question: string;
          answer: { __typename?: "GCMS_RichText"; markdown: string };
        }>;
      }
    | {
        __typename: "GCMS_FeaturedCarousel";
        header?: string | null;
        bg: Gcms_ThemeColor;
        color: Gcms_ThemeColor;
      }
    | {
        __typename: "GCMS_Grid";
        id: string;
        title?: string | null;
        headline?: string | null;
        subtitle?: string | null;
        slug?: string | null;
        columnComponent?: Gcms_GridComponent | null;
        gridProperties?: any | null;
        bg: Gcms_ThemeColor;
        color: Gcms_ThemeColor;
        gridItems: Array<
          | {
              __typename: "GCMS_Faq";
              id: string;
              question: string;
              answer: { __typename?: "GCMS_RichText"; markdown: string };
            }
          | {
              __typename: "GCMS_FeatureIcon";
              id: string;
              title: string;
              icon: string;
              layout: Gcms_GridLayout;
              content: { __typename?: "GCMS_RichText"; markdown: string };
            }
          | {
              __typename: "GCMS_Person";
              id: string;
              name: string;
              role?: string | null;
              blurb?: string | null;
              teamMember: boolean;
              photo?: {
                __typename?: "GCMS_Asset";
                id: string;
                url: string;
                width?: number | null;
                height?: number | null;
                handle: string;
              } | null;
              bio?: { __typename?: "GCMS_RichText"; markdown: string } | null;
            }
        >;
        image?: {
          __typename?: "GCMS_Asset";
          id: string;
          url: string;
          width?: number | null;
          height?: number | null;
          handle: string;
        } | null;
      }
    | {
        __typename: "GCMS_InstagramDisplay";
        title?: string | null;
        backgroundColor: Gcms_ThemeColor;
        textColor: Gcms_ThemeColor;
      }
    | {
        __typename: "GCMS_JobsSection";
        description: string;
        noJobsText: string;
        equalOpportunityText: string;
        color: Gcms_ThemeColor;
        bg: Gcms_ThemeColor;
        jobPostings: Array<{
          __typename?: "GCMS_JobPosting";
          jobTitle: string;
          jobDescription: string;
        }>;
      }
    | {
        __typename: "GCMS_KnowledgeCenterTeaser";
        title?: string | null;
        subtitle?: string | null;
        bg: Gcms_ThemeColor;
        color: Gcms_ThemeColor;
        teaserCards: Array<{
          __typename?: "GCMS_KnowledgeCenterTeaserCard";
          link: string;
          displayName?: string | null;
          description?: string | null;
          image: {
            __typename?: "GCMS_Asset";
            id: string;
            url: string;
            width?: number | null;
            height?: number | null;
            handle: string;
          };
        }>;
      }
    | {
        __typename: "GCMS_LogoGallery";
        backgroundColor: Gcms_ThemeColor;
        textColor: Gcms_ThemeColor;
        title?: string | null;
        shouldLink?: boolean | null;
      }
    | {
        __typename: "GCMS_NewsletterSection";
        title?: string | null;
        subtitle?: string | null;
        bg: Gcms_ThemeColor;
        color: Gcms_ThemeColor;
      }
    | {
        __typename: "GCMS_PodcastHomePlayer";
        header?: string | null;
        title?: string | null;
        subtitle?: string | null;
        mobileSubtitle?: string | null;
        bg: Gcms_ThemeColor;
        color: Gcms_ThemeColor;
      }
    | {
        __typename: "GCMS_PodcastPlayerSection";
        title?: string | null;
        backgroundColor: Gcms_ThemeColor;
        textColor: Gcms_ThemeColor;
        podcastPlayerPosition: Gcms_ContentPosition;
        content?: { __typename?: "GCMS_RichText"; markdown: string } | null;
      }
    | {
        __typename: "GCMS_PressItemSection";
        backgroundColor: Gcms_ThemeColor;
        textColor: Gcms_ThemeColor;
        pressItems: Array<{
          __typename: "GCMS_PressItem";
          date?: any | null;
          description: string;
          title: string;
          type: Gcms_PressItemType;
          info?: {
            __typename: "GCMS_PressItem_Link";
            link: string;
          } | null;
          logo?: { __typename?: "GCMS_Asset"; url: string } | null;
        }>;
      }
    | {
        __typename: "GCMS_ResourcesSection";
        id: string;
        title?: string | null;
        bg: Gcms_ThemeColor;
        color: Gcms_ThemeColor;
        content?: { __typename?: "GCMS_RichText"; markdown: string } | null;
        resources: Array<{
          __typename?: "GCMS_Resource";
          id: string;
          slug?: string | null;
          title?: string | null;
          subtitle?: string | null;
          bg: Gcms_ThemeColor;
          thumbnail?: {
            __typename?: "GCMS_Asset";
            id: string;
            url: string;
            width?: number | null;
            height?: number | null;
            handle: string;
          } | null;
        }>;
      }
    | {
        __typename: "GCMS_Section";
        id: string;
        title?: string | null;
        subtitle?: string | null;
        contentPosition: Gcms_ContentPosition;
        bg: Gcms_ThemeColor;
        color: Gcms_ThemeColor;
        textAlignment?: Gcms_ContentPosition | null;
        smallText?: boolean | null;
        titleColor?: Gcms_ThemeColor | null;
        rounded?: boolean | null;
        image?: {
          __typename?: "GCMS_Asset";
          id: string;
          url: string;
          width?: number | null;
          height?: number | null;
          handle: string;
        } | null;
        content?: {
          __typename?: "GCMS_SectionContentRichText";
          markdown: string;
        } | null;
        buttons: Array<{
          __typename?: "GCMS_Button";
          id: string;
          href?: string | null;
          label: string;
          theme?: Gcms_ButtonTheme | null;
          buttonAction: Gcms_ButtonAction;
          variant: Gcms_ButtonVariants;
          linkedPage?: {
            __typename?: "GCMS_Page";
            id: string;
            slug: string;
          } | null;
        }>;
      }
    | {
        __typename: "GCMS_SimplePageHeader";
        title?: string | null;
        backgroundColor: Gcms_ThemeColor;
        textColor: Gcms_ThemeColor;
        content?: { __typename?: "GCMS_RichText"; markdown: string } | null;
        buttons: Array<{
          __typename?: "GCMS_Button";
          id: string;
          href?: string | null;
          label: string;
          theme?: Gcms_ButtonTheme | null;
          buttonAction: Gcms_ButtonAction;
          variant: Gcms_ButtonVariants;
          linkedPage?: {
            __typename?: "GCMS_Page";
            id: string;
            slug: string;
          } | null;
        }>;
      }
    | {
        __typename: "GCMS_StatsBlock";
        title?: string | null;
        subtitle?: string | null;
        bg: Gcms_ThemeColor;
        color: Gcms_ThemeColor;
        stats: Array<{
          __typename?: "GCMS_Stat";
          stat?: string | null;
          desc?: string | null;
          citation?: string | null;
        }>;
      }
    | {
        __typename: "GCMS_TestimonialSection";
        bg: Gcms_ThemeColor;
        color: Gcms_ThemeColor;
        testimonials: Array<{
          __typename?: "GCMS_Testimonial";
          name: string;
          jobTitle?: string | null;
          content?: { __typename?: "GCMS_RichText"; raw: any } | null;
          picture?: {
            __typename?: "GCMS_Asset";
            id: string;
            url: string;
            width?: number | null;
            height?: number | null;
            handle: string;
          } | null;
        }>;
      }
  >;
  marketing: Array<
    | {
        __typename: "GCMS_Banner";
        id: string;
        bg: Gcms_ThemeColor;
        color: Gcms_ThemeColor;
        message: { __typename?: "GCMS_RichText"; markdown: string };
        button?: {
          __typename?: "GCMS_Button";
          id: string;
          href?: string | null;
          label: string;
          theme?: Gcms_ButtonTheme | null;
          buttonAction: Gcms_ButtonAction;
          variant: Gcms_ButtonVariants;
          linkedPage?: {
            __typename?: "GCMS_Page";
            id: string;
            slug: string;
          } | null;
        } | null;
      }
    | {
        __typename: "GCMS_PopUp";
        id: string;
        title: string;
        content?: string | null;
        buttons: Array<{
          __typename?: "GCMS_Button";
          id: string;
          href?: string | null;
          label: string;
          theme?: Gcms_ButtonTheme | null;
          buttonAction: Gcms_ButtonAction;
          variant: Gcms_ButtonVariants;
          linkedPage?: {
            __typename?: "GCMS_Page";
            id: string;
            slug: string;
          } | null;
        }>;
      }
  >;
  seo?: {
    __typename?: "GCMS_Seo";
    description?: string | null;
    title?: string | null;
    keywords: Array<string>;
    image?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
  } | null;
  navBar?: {
    __typename?: "GCMS_NavBar";
    buttons: Array<{
      __typename?: "GCMS_Button";
      id: string;
      href?: string | null;
      label: string;
      theme?: Gcms_ButtonTheme | null;
      buttonAction: Gcms_ButtonAction;
      variant: Gcms_ButtonVariants;
      linkedPage?: {
        __typename?: "GCMS_Page";
        id: string;
        slug: string;
      } | null;
    }>;
    links: Array<{
      __typename?: "GCMS_Link";
      name?: string | null;
      url?: string | null;
      isInternal?: boolean | null;
    }>;
  } | null;
  footer?: {
    __typename?: "GCMS_Footer";
    copyright?: string | null;
    links: Array<{
      __typename?: "GCMS_Link";
      name?: string | null;
      url?: string | null;
      isInternal?: boolean | null;
    }>;
    bottomLinks: Array<{
      __typename?: "GCMS_Link";
      name?: string | null;
      url?: string | null;
      isInternal?: boolean | null;
    }>;
  } | null;
};

export type GcmsEnterpriseContactPageFragment = {
  __typename?: "GCMS_EnterpriseContactPage";
  title?: string | null;
  subtitle?: string | null;
  caption?: string | null;
  seo?: {
    __typename?: "GCMS_Seo";
    description?: string | null;
    title?: string | null;
    keywords: Array<string>;
    image?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
  } | null;
  contactForm?: {
    __typename?: "GCMS_EnterpriseContactForm";
    id: string;
    inputFields: Array<{
      __typename?: "GCMS_EnterpriseInputField";
      htmlFor?: string | null;
      label?: string | null;
      type?: string | null;
      placeholder?: string | null;
    }>;
    selectOptions: Array<{
      __typename?: "GCMS_EnterpriseSelectField";
      option?: string | null;
    }>;
    checkbox?: {
      __typename?: "GCMS_EnterpriseCheckbox";
      content?: string | null;
    } | null;
  } | null;
};

export type GcmsEnterpriseContactFormFragment = {
  __typename?: "GCMS_EnterpriseContactForm";
  id: string;
  inputFields: Array<{
    __typename?: "GCMS_EnterpriseInputField";
    htmlFor?: string | null;
    label?: string | null;
    type?: string | null;
    placeholder?: string | null;
  }>;
  selectOptions: Array<{
    __typename?: "GCMS_EnterpriseSelectField";
    option?: string | null;
  }>;
  checkbox?: {
    __typename?: "GCMS_EnterpriseCheckbox";
    content?: string | null;
  } | null;
};

export type GcmsEnterpriseInputFieldFragment = {
  __typename?: "GCMS_EnterpriseInputField";
  htmlFor?: string | null;
  label?: string | null;
  type?: string | null;
  placeholder?: string | null;
};

export type GcmsEnterpriseSelectFieldFragment = {
  __typename?: "GCMS_EnterpriseSelectField";
  option?: string | null;
};

export type GcmsEnterpriseCheckboxFragment = {
  __typename?: "GCMS_EnterpriseCheckbox";
  content?: string | null;
};

export type GcmsFaqSectionFragment = {
  __typename: "GCMS_FaqSection";
  id: string;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  header?: string | null;
  faqs: Array<{
    __typename: "GCMS_Faq";
    id: string;
    question: string;
    answer: { __typename?: "GCMS_RichText"; markdown: string };
  }>;
};

export type GcmsFaqEmbedFragment = {
  __typename: "GCMS_FaqSection";
  id: string;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  header?: string | null;
  faqs: Array<{
    __typename: "GCMS_Faq";
    id: string;
    question: string;
    answer: { __typename?: "GCMS_RichText"; text: string; raw: any };
  }>;
};

export type GcmsFeaturedCarouselFragment = {
  __typename: "GCMS_FeaturedCarousel";
  header?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
};

export type GcmsFooterFragment = {
  __typename?: "GCMS_Footer";
  copyright?: string | null;
  links: Array<{
    __typename?: "GCMS_Link";
    name?: string | null;
    url?: string | null;
    isInternal?: boolean | null;
  }>;
  bottomLinks: Array<{
    __typename?: "GCMS_Link";
    name?: string | null;
    url?: string | null;
    isInternal?: boolean | null;
  }>;
};

export type GcmsFormFragment = {
  __typename?: "GCMS_Form";
  id: string;
  bg?: Gcms_ThemeColor | null;
  color?: Gcms_ThemeColor | null;
  title: string;
  slug?: string | null;
  successMessage?: { __typename?: "GCMS_RichText"; markdown: string } | null;
  fields: Array<
    | {
        __typename: "GCMS_FormCheckbox";
        name?: string | null;
        label?: string | null;
        required?: boolean | null;
        formName?: string | null;
      }
    | {
        __typename: "GCMS_FormInput";
        name?: string | null;
        type?: Gcms_FormInputType | null;
        label?: string | null;
        placeholder?: string | null;
        required?: boolean | null;
        formName?: string | null;
      }
    | {
        __typename: "GCMS_FormSelect";
        name?: string | null;
        formName?: string | null;
        label?: string | null;
        required?: boolean | null;
        choices: Array<{
          __typename?: "GCMS_FormOption";
          value?: string | null;
          option?: string | null;
        }>;
      }
    | {
        __typename: "GCMS_FormTextarea";
        name?: string | null;
        label?: string | null;
        placeholder?: string | null;
        required?: boolean | null;
        formName?: string | null;
      }
  >;
  submitButton?: {
    __typename?: "GCMS_Button";
    id: string;
    label: string;
    variant: Gcms_ButtonVariants;
    theme?: Gcms_ButtonTheme | null;
    buttonAction: Gcms_ButtonAction;
  } | null;
};

export type GcmsGridFeatureIconFragment = {
  __typename: "GCMS_FeatureIcon";
  id: string;
  title: string;
  icon: string;
  layout: Gcms_GridLayout;
  content: { __typename?: "GCMS_RichText"; markdown: string };
};

export type GcmsGridPersonFragment = {
  __typename: "GCMS_Person";
  id: string;
  name: string;
  role?: string | null;
  blurb?: string | null;
  teamMember: boolean;
  photo?: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
  bio?: { __typename?: "GCMS_RichText"; markdown: string } | null;
};

export type GcmsGridFragment = {
  __typename: "GCMS_Grid";
  id: string;
  title?: string | null;
  headline?: string | null;
  subtitle?: string | null;
  slug?: string | null;
  columnComponent?: Gcms_GridComponent | null;
  gridProperties?: any | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  gridItems: Array<
    | {
        __typename: "GCMS_Faq";
        id: string;
        question: string;
        answer: { __typename?: "GCMS_RichText"; markdown: string };
      }
    | {
        __typename: "GCMS_FeatureIcon";
        id: string;
        title: string;
        icon: string;
        layout: Gcms_GridLayout;
        content: { __typename?: "GCMS_RichText"; markdown: string };
      }
    | {
        __typename: "GCMS_Person";
        id: string;
        name: string;
        role?: string | null;
        blurb?: string | null;
        teamMember: boolean;
        photo?: {
          __typename?: "GCMS_Asset";
          id: string;
          url: string;
          width?: number | null;
          height?: number | null;
          handle: string;
        } | null;
        bio?: { __typename?: "GCMS_RichText"; markdown: string } | null;
      }
  >;
  image?: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
};

export type GcmsGridFaqFragment = {
  __typename: "GCMS_Faq";
  id: string;
  question: string;
  answer: { __typename?: "GCMS_RichText"; markdown: string };
};

export type GcmsHeroFragment = {
  __typename?: "GCMS_Hero";
  slug?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  title?: string | null;
  subtitle?: string | null;
  pushText?: string | null;
  image?: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
  mobileImage?: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
  buttons: Array<{
    __typename?: "GCMS_Button";
    id: string;
    href?: string | null;
    label: string;
    theme?: Gcms_ButtonTheme | null;
    buttonAction: Gcms_ButtonAction;
    variant: Gcms_ButtonVariants;
    linkedPage?: { __typename?: "GCMS_Page"; id: string; slug: string } | null;
  }>;
};

export type GcmsImageFragment = {
  __typename?: "GCMS_Asset";
  id: string;
  url: string;
  width?: number | null;
  height?: number | null;
  handle: string;
};

export type GcmsInstagramDisplayFragment = {
  __typename: "GCMS_InstagramDisplay";
  title?: string | null;
  backgroundColor: Gcms_ThemeColor;
  textColor: Gcms_ThemeColor;
};

export type GcmsJobPostingFragment = {
  __typename?: "GCMS_JobPosting";
  jobTitle: string;
  jobDescription: string;
};

export type GcmsJobsSectionFragment = {
  __typename: "GCMS_JobsSection";
  description: string;
  noJobsText: string;
  equalOpportunityText: string;
  color: Gcms_ThemeColor;
  bg: Gcms_ThemeColor;
  jobPostings: Array<{
    __typename?: "GCMS_JobPosting";
    jobTitle: string;
    jobDescription: string;
  }>;
};

export type GcmsKnowledgeCenterTeaserFragment = {
  __typename: "GCMS_KnowledgeCenterTeaser";
  title?: string | null;
  subtitle?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  teaserCards: Array<{
    __typename?: "GCMS_KnowledgeCenterTeaserCard";
    link: string;
    displayName?: string | null;
    description?: string | null;
    image: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    };
  }>;
};

export type GcmsKnowledgeCenterTeaserCardFragment = {
  __typename?: "GCMS_KnowledgeCenterTeaserCard";
  link: string;
  displayName?: string | null;
  description?: string | null;
  image: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  };
};

export type GcmsLinkFragment = {
  __typename?: "GCMS_Link";
  name?: string | null;
  url?: string | null;
  isInternal?: boolean | null;
};

export type GcmsLinkGroupFragment = {
  __typename?: "GCMS_LinkGroup";
  id: string;
  title: string;
  links: Array<{
    __typename?: "GCMS_Link";
    name?: string | null;
    url?: string | null;
    isInternal?: boolean | null;
  }>;
};

export type GcmsLinkedPageFragment = {
  __typename?: "GCMS_Page";
  id: string;
  slug: string;
};

export type GcmsLogoGalleryFragment = {
  __typename: "GCMS_LogoGallery";
  backgroundColor: Gcms_ThemeColor;
  textColor: Gcms_ThemeColor;
  title?: string | null;
  shouldLink?: boolean | null;
};
type GcmsMarketing_Banner_Fragment = {
  __typename: "GCMS_Banner";
  id: string;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  message: { __typename?: "GCMS_RichText"; markdown: string };
  button?: {
    __typename?: "GCMS_Button";
    id: string;
    href?: string | null;
    label: string;
    theme?: Gcms_ButtonTheme | null;
    buttonAction: Gcms_ButtonAction;
    variant: Gcms_ButtonVariants;
    linkedPage?: { __typename?: "GCMS_Page"; id: string; slug: string } | null;
  } | null;
};
type GcmsMarketing_PopUp_Fragment = {
  __typename: "GCMS_PopUp";
  id: string;
  title: string;
  content?: string | null;
  buttons: Array<{
    __typename?: "GCMS_Button";
    id: string;
    href?: string | null;
    label: string;
    theme?: Gcms_ButtonTheme | null;
    buttonAction: Gcms_ButtonAction;
    variant: Gcms_ButtonVariants;
    linkedPage?: { __typename?: "GCMS_Page"; id: string; slug: string } | null;
  }>;
};

export type GcmsMarketingFragment =
  | GcmsMarketing_Banner_Fragment
  | GcmsMarketing_PopUp_Fragment;

export type GcmsNavBarFragment = {
  __typename?: "GCMS_NavBar";
  buttons: Array<{
    __typename?: "GCMS_Button";
    id: string;
    href?: string | null;
    label: string;
    theme?: Gcms_ButtonTheme | null;
    buttonAction: Gcms_ButtonAction;
    variant: Gcms_ButtonVariants;
    linkedPage?: { __typename?: "GCMS_Page"; id: string; slug: string } | null;
  }>;
  links: Array<{
    __typename?: "GCMS_Link";
    name?: string | null;
    url?: string | null;
    isInternal?: boolean | null;
  }>;
};

export type GcmsNewsletterSectionFragment = {
  __typename: "GCMS_NewsletterSection";
  title?: string | null;
  subtitle?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
};
type GcmsPageBlock_Breakpoint_Fragment = {
  __typename: "GCMS_Breakpoint";
  id: string;
  title?: string | null;
  highlight?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  content?: {
    __typename?: "GCMS_BreakpointContentRichText";
    markdown: string;
  } | null;
  buttons: Array<{
    __typename?: "GCMS_Button";
    id: string;
    href?: string | null;
    label: string;
    theme?: Gcms_ButtonTheme | null;
    buttonAction: Gcms_ButtonAction;
    variant: Gcms_ButtonVariants;
    linkedPage?: { __typename?: "GCMS_Page"; id: string; slug: string } | null;
  }>;
};
type GcmsPageBlock_DatasetTeaser_Fragment = {
  __typename: "GCMS_DatasetTeaser";
  title?: string | null;
  subtitle?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  categories: Array<{
    __typename?: "GCMS_Category";
    id: string;
    key: Gcms_ParentCategory;
    displayName: string;
    descriptionMobile: string;
    description: string;
    statCardBg?: Gcms_ThemeColor | null;
    knowledgeBaseSlug: string;
    subcategories: Array<{
      __typename?: "GCMS_Subcategory";
      id: string;
      key: Gcms_SubcategoryType;
      displayName?: string | null;
    }>;
  }>;
};
type GcmsPageBlock_FaqSection_Fragment = {
  __typename: "GCMS_FaqSection";
  id: string;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  header?: string | null;
  faqs: Array<{
    __typename: "GCMS_Faq";
    id: string;
    question: string;
    answer: { __typename?: "GCMS_RichText"; markdown: string };
  }>;
};
type GcmsPageBlock_FeaturedCarousel_Fragment = {
  __typename: "GCMS_FeaturedCarousel";
  header?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
};
type GcmsPageBlock_Grid_Fragment = {
  __typename: "GCMS_Grid";
  id: string;
  title?: string | null;
  headline?: string | null;
  subtitle?: string | null;
  slug?: string | null;
  columnComponent?: Gcms_GridComponent | null;
  gridProperties?: any | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  gridItems: Array<
    | {
        __typename: "GCMS_Faq";
        id: string;
        question: string;
        answer: { __typename?: "GCMS_RichText"; markdown: string };
      }
    | {
        __typename: "GCMS_FeatureIcon";
        id: string;
        title: string;
        icon: string;
        layout: Gcms_GridLayout;
        content: { __typename?: "GCMS_RichText"; markdown: string };
      }
    | {
        __typename: "GCMS_Person";
        id: string;
        name: string;
        role?: string | null;
        blurb?: string | null;
        teamMember: boolean;
        photo?: {
          __typename?: "GCMS_Asset";
          id: string;
          url: string;
          width?: number | null;
          height?: number | null;
          handle: string;
        } | null;
        bio?: { __typename?: "GCMS_RichText"; markdown: string } | null;
      }
  >;
  image?: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
};
type GcmsPageBlock_InstagramDisplay_Fragment = {
  __typename: "GCMS_InstagramDisplay";
  title?: string | null;
  backgroundColor: Gcms_ThemeColor;
  textColor: Gcms_ThemeColor;
};
type GcmsPageBlock_JobsSection_Fragment = {
  __typename: "GCMS_JobsSection";
  description: string;
  noJobsText: string;
  equalOpportunityText: string;
  color: Gcms_ThemeColor;
  bg: Gcms_ThemeColor;
  jobPostings: Array<{
    __typename?: "GCMS_JobPosting";
    jobTitle: string;
    jobDescription: string;
  }>;
};
type GcmsPageBlock_KnowledgeCenterTeaser_Fragment = {
  __typename: "GCMS_KnowledgeCenterTeaser";
  title?: string | null;
  subtitle?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  teaserCards: Array<{
    __typename?: "GCMS_KnowledgeCenterTeaserCard";
    link: string;
    displayName?: string | null;
    description?: string | null;
    image: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    };
  }>;
};
type GcmsPageBlock_LogoGallery_Fragment = {
  __typename: "GCMS_LogoGallery";
  backgroundColor: Gcms_ThemeColor;
  textColor: Gcms_ThemeColor;
  title?: string | null;
  shouldLink?: boolean | null;
};
type GcmsPageBlock_NewsletterSection_Fragment = {
  __typename: "GCMS_NewsletterSection";
  title?: string | null;
  subtitle?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
};
type GcmsPageBlock_PodcastHomePlayer_Fragment = {
  __typename: "GCMS_PodcastHomePlayer";
  header?: string | null;
  title?: string | null;
  subtitle?: string | null;
  mobileSubtitle?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
};
type GcmsPageBlock_PodcastPlayerSection_Fragment = {
  __typename: "GCMS_PodcastPlayerSection";
  title?: string | null;
  backgroundColor: Gcms_ThemeColor;
  textColor: Gcms_ThemeColor;
  podcastPlayerPosition: Gcms_ContentPosition;
  content?: { __typename?: "GCMS_RichText"; markdown: string } | null;
};
type GcmsPageBlock_PressItemSection_Fragment = {
  __typename: "GCMS_PressItemSection";
  backgroundColor: Gcms_ThemeColor;
  textColor: Gcms_ThemeColor;
  pressItems: Array<{
    __typename: "GCMS_PressItem";
    date?: any | null;
    description: string;
    title: string;
    type: Gcms_PressItemType;
    info?: { __typename: "GCMS_PressItem_Link"; link: string } | null;
    logo?: { __typename?: "GCMS_Asset"; url: string } | null;
  }>;
};
type GcmsPageBlock_ResourcesSection_Fragment = {
  __typename: "GCMS_ResourcesSection";
  id: string;
  title?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  content?: { __typename?: "GCMS_RichText"; markdown: string } | null;
  resources: Array<{
    __typename?: "GCMS_Resource";
    id: string;
    slug?: string | null;
    title?: string | null;
    subtitle?: string | null;
    bg: Gcms_ThemeColor;
    thumbnail?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
  }>;
};
type GcmsPageBlock_Section_Fragment = {
  __typename: "GCMS_Section";
  id: string;
  title?: string | null;
  subtitle?: string | null;
  contentPosition: Gcms_ContentPosition;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  textAlignment?: Gcms_ContentPosition | null;
  smallText?: boolean | null;
  titleColor?: Gcms_ThemeColor | null;
  rounded?: boolean | null;
  image?: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
  content?: {
    __typename?: "GCMS_SectionContentRichText";
    markdown: string;
  } | null;
  buttons: Array<{
    __typename?: "GCMS_Button";
    id: string;
    href?: string | null;
    label: string;
    theme?: Gcms_ButtonTheme | null;
    buttonAction: Gcms_ButtonAction;
    variant: Gcms_ButtonVariants;
    linkedPage?: { __typename?: "GCMS_Page"; id: string; slug: string } | null;
  }>;
};
type GcmsPageBlock_SimplePageHeader_Fragment = {
  __typename: "GCMS_SimplePageHeader";
  title?: string | null;
  backgroundColor: Gcms_ThemeColor;
  textColor: Gcms_ThemeColor;
  content?: { __typename?: "GCMS_RichText"; markdown: string } | null;
  buttons: Array<{
    __typename?: "GCMS_Button";
    id: string;
    href?: string | null;
    label: string;
    theme?: Gcms_ButtonTheme | null;
    buttonAction: Gcms_ButtonAction;
    variant: Gcms_ButtonVariants;
    linkedPage?: { __typename?: "GCMS_Page"; id: string; slug: string } | null;
  }>;
};
type GcmsPageBlock_StatsBlock_Fragment = {
  __typename: "GCMS_StatsBlock";
  title?: string | null;
  subtitle?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  stats: Array<{
    __typename?: "GCMS_Stat";
    stat?: string | null;
    desc?: string | null;
    citation?: string | null;
  }>;
};
type GcmsPageBlock_TestimonialSection_Fragment = {
  __typename: "GCMS_TestimonialSection";
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  testimonials: Array<{
    __typename?: "GCMS_Testimonial";
    name: string;
    jobTitle?: string | null;
    content?: { __typename?: "GCMS_RichText"; raw: any } | null;
    picture?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
  }>;
};

export type GcmsPageBlockFragment =
  | GcmsPageBlock_Breakpoint_Fragment
  | GcmsPageBlock_DatasetTeaser_Fragment
  | GcmsPageBlock_FaqSection_Fragment
  | GcmsPageBlock_FeaturedCarousel_Fragment
  | GcmsPageBlock_Grid_Fragment
  | GcmsPageBlock_InstagramDisplay_Fragment
  | GcmsPageBlock_JobsSection_Fragment
  | GcmsPageBlock_KnowledgeCenterTeaser_Fragment
  | GcmsPageBlock_LogoGallery_Fragment
  | GcmsPageBlock_NewsletterSection_Fragment
  | GcmsPageBlock_PodcastHomePlayer_Fragment
  | GcmsPageBlock_PodcastPlayerSection_Fragment
  | GcmsPageBlock_PressItemSection_Fragment
  | GcmsPageBlock_ResourcesSection_Fragment
  | GcmsPageBlock_Section_Fragment
  | GcmsPageBlock_SimplePageHeader_Fragment
  | GcmsPageBlock_StatsBlock_Fragment
  | GcmsPageBlock_TestimonialSection_Fragment;

export type GcmsPodcastHomePlayerFragment = {
  __typename: "GCMS_PodcastHomePlayer";
  header?: string | null;
  title?: string | null;
  subtitle?: string | null;
  mobileSubtitle?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
};

export type GcmsPodcastNewsBriefFragment = {
  __typename?: "GCMS_PodcastNewsBrief";
  id: string;
  link?: string | null;
};

export type GcmsPodcastPlayerSectionFragment = {
  __typename: "GCMS_PodcastPlayerSection";
  title?: string | null;
  backgroundColor: Gcms_ThemeColor;
  textColor: Gcms_ThemeColor;
  podcastPlayerPosition: Gcms_ContentPosition;
  content?: { __typename?: "GCMS_RichText"; markdown: string } | null;
};

export type GcmsPopupFragment = {
  __typename: "GCMS_PopUp";
  id: string;
  title: string;
  content?: string | null;
  buttons: Array<{
    __typename?: "GCMS_Button";
    id: string;
    href?: string | null;
    label: string;
    theme?: Gcms_ButtonTheme | null;
    buttonAction: Gcms_ButtonAction;
    variant: Gcms_ButtonVariants;
    linkedPage?: { __typename?: "GCMS_Page"; id: string; slug: string } | null;
  }>;
};

export type GcmsPostFragment = {
  __typename?: "GCMS_Post";
  tags: Array<string>;
  title: string;
  slug: string;
  createdAt: any;
  updatedAt: any;
  published: any;
  excerpt?: string | null;
  readingTime: number;
  postType: Gcms_PostType;
  trending?: boolean | null;
  author?: {
    __typename?: "GCMS_Author";
    slug: string;
    person?: {
      __typename?: "GCMS_Person";
      name: string;
      photo?: {
        __typename?: "GCMS_Asset";
        id: string;
        url: string;
        width?: number | null;
        height?: number | null;
        handle: string;
      } | null;
    } | null;
    posts: Array<{
      __typename?: "GCMS_Post";
      title: string;
      slug: string;
      createdAt: any;
      updatedAt: any;
      published: any;
      excerpt?: string | null;
      readingTime: number;
      postType: Gcms_PostType;
      trending?: boolean | null;
      coverImage?: {
        __typename?: "GCMS_Asset";
        id: string;
        url: string;
        width?: number | null;
        height?: number | null;
        handle: string;
      } | null;
      aggregatePostPage?: {
        __typename?: "GCMS_AggregatePostPage";
        title?: string | null;
        slug: string;
      } | null;
    }>;
  } | null;
  content: {
    __typename?: "GCMS_PostContentRichText";
    raw: any;
    markdown: string;
    json: any;
    references: Array<
      | {
          __typename?: "GCMS_AdvertisingList";
          id: string;
          listItem: Array<string>;
        }
      | {
          __typename?: "GCMS_Asset";
          mimeType?: string | null;
          id: string;
          url: string;
          width?: number | null;
          height?: number | null;
          handle: string;
        }
      | {
          __typename?: "GCMS_BlockquoteComponent";
          id: string;
          description?: { __typename?: "GCMS_RichText"; raw: any } | null;
        }
      | {
          __typename?: "GCMS_EnterpriseContactForm";
          id: string;
          inputFields: Array<{
            __typename?: "GCMS_EnterpriseInputField";
            htmlFor?: string | null;
            label?: string | null;
            type?: string | null;
            placeholder?: string | null;
          }>;
          selectOptions: Array<{
            __typename?: "GCMS_EnterpriseSelectField";
            option?: string | null;
          }>;
          checkbox?: {
            __typename?: "GCMS_EnterpriseCheckbox";
            content?: string | null;
          } | null;
        }
      | {
          __typename: "GCMS_FaqSection";
          id: string;
          bg: Gcms_ThemeColor;
          color: Gcms_ThemeColor;
          header?: string | null;
          faqs: Array<{
            __typename: "GCMS_Faq";
            id: string;
            question: string;
            answer: { __typename?: "GCMS_RichText"; text: string; raw: any };
          }>;
        }
      | {
          __typename?: "GCMS_PodcastNewsBrief";
          id: string;
          link?: string | null;
        }
      | {
          __typename?: "GCMS_ProductReviewCard";
          id: string;
          name: string;
          priceRating: number;
          imageUrl: string;
          href: string;
          buttonLabel?: string | null;
          tag?: {
            __typename?: "GCMS_ProductReviewTag";
            name: string;
            color: Gcms_ButtonTheme;
          } | null;
        }
      | {
          __typename?: "GCMS_ProductReviewGrid";
          id: string;
          title: string;
          description?: { __typename?: "GCMS_RichText"; raw: any } | null;
          disclaimer?: { __typename?: "GCMS_RichText"; raw: any } | null;
          products: Array<{
            __typename?: "GCMS_ProductReviewCard";
            id: string;
            name: string;
            priceRating: number;
            imageUrl: string;
            href: string;
            buttonLabel?: string | null;
            tag?: {
              __typename?: "GCMS_ProductReviewTag";
              name: string;
              color: Gcms_ButtonTheme;
            } | null;
          }>;
        }
      | {
          __typename?: "GCMS_ReportCta";
          id: string;
          freeReportButton?: string | null;
          plansButton?: string | null;
          description?: { __typename?: "GCMS_RichText"; raw: any } | null;
        }
    >;
  };
  seo?: {
    __typename?: "GCMS_Seo";
    description?: string | null;
    title?: string | null;
    keywords: Array<string>;
    image?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
  } | null;
  coverImage?: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
  aggregatePostPage?: {
    __typename?: "GCMS_AggregatePostPage";
    title?: string | null;
    slug: string;
  } | null;
};

export type GcmsPostPreviewFragment = {
  __typename?: "GCMS_Post";
  title: string;
  slug: string;
  createdAt: any;
  updatedAt: any;
  published: any;
  excerpt?: string | null;
  readingTime: number;
  postType: Gcms_PostType;
  trending?: boolean | null;
  author?: {
    __typename?: "GCMS_Author";
    slug: string;
    person?: {
      __typename?: "GCMS_Person";
      name: string;
      photo?: {
        __typename?: "GCMS_Asset";
        id: string;
        url: string;
        width?: number | null;
        height?: number | null;
        handle: string;
      } | null;
    } | null;
  } | null;
  coverImage?: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
  aggregatePostPage?: {
    __typename?: "GCMS_AggregatePostPage";
    title?: string | null;
    slug: string;
  } | null;
};

export type GcmsPostPreviewNonCyclicalFragment = {
  __typename?: "GCMS_Post";
  title: string;
  slug: string;
  createdAt: any;
  updatedAt: any;
  published: any;
  excerpt?: string | null;
  readingTime: number;
  postType: Gcms_PostType;
  trending?: boolean | null;
  coverImage?: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
  aggregatePostPage?: {
    __typename?: "GCMS_AggregatePostPage";
    title?: string | null;
    slug: string;
  } | null;
};

export type GcmsPressItemFragment = {
  __typename: "GCMS_PressItem";
  date?: any | null;
  description: string;
  title: string;
  type: Gcms_PressItemType;
  info?: { __typename: "GCMS_PressItem_Link"; link: string } | null;
  logo?: { __typename?: "GCMS_Asset"; url: string } | null;
};

export type GcmsPressItemLinkFragment = {
  __typename: "GCMS_PressItem_Link";
  link: string;
};

export type GcmsPressItemSectionFragment = {
  __typename: "GCMS_PressItemSection";
  backgroundColor: Gcms_ThemeColor;
  textColor: Gcms_ThemeColor;
  pressItems: Array<{
    __typename: "GCMS_PressItem";
    date?: any | null;
    description: string;
    title: string;
    type: Gcms_PressItemType;
    info?: { __typename: "GCMS_PressItem_Link"; link: string } | null;
    logo?: { __typename?: "GCMS_Asset"; url: string } | null;
  }>;
};

export type GcmsPricingPageFragment = {
  __typename?: "GCMS_Pricing";
  seo?: {
    __typename?: "GCMS_Seo";
    description?: string | null;
    title?: string | null;
    keywords: Array<string>;
    image?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
  } | null;
  section?: {
    __typename?: "GCMS_PricingSection";
    title?: string | null;
    bg?: Gcms_ThemeColor | null;
    mobileHeroImage?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
    subtitle?: { __typename?: "GCMS_RichText"; raw: any } | null;
    cards: Array<{
      __typename?: "GCMS_PricingCard";
      id: string;
      plan?: string | null;
      title?: string | null;
      price?: string | null;
      mobileSubtitle?: string | null;
      desktopSubtitle?: string | null;
      button?: string | null;
      planImage?: {
        __typename?: "GCMS_Asset";
        id: string;
        url: string;
        width?: number | null;
        height?: number | null;
        handle: string;
      } | null;
      featureList: Array<{
        __typename?: "GCMS_PricingFeatureList";
        id: string;
        feature?: { __typename?: "GCMS_RichText"; raw: any } | null;
      }>;
    }>;
  } | null;
  table?: {
    __typename?: "GCMS_PricingTable";
    title?: string | null;
    subtitle?: { __typename?: "GCMS_RichText"; raw: any } | null;
    pricingIds: Array<{
      __typename?: "GCMS_PricingId";
      name?: string | null;
      planId?: string | null;
      testId?: string | null;
    }>;
    caption?: { __typename?: "GCMS_RichText"; raw: any } | null;
  } | null;
  faqSection?: {
    __typename?: "GCMS_FaqSection";
    title?: string | null;
    bg: Gcms_ThemeColor;
    color: Gcms_ThemeColor;
    faqs: Array<{
      __typename?: "GCMS_Faq";
      question: string;
      answer: { __typename?: "GCMS_RichText"; text: string; raw: any };
    }>;
  } | null;
};

export type GcmsPricingFeatureListFragment = {
  __typename?: "GCMS_PricingFeatureList";
  id: string;
  feature?: { __typename?: "GCMS_RichText"; raw: any } | null;
};

export type GcmsProductReviewCardFragment = {
  __typename?: "GCMS_ProductReviewCard";
  id: string;
  name: string;
  priceRating: number;
  imageUrl: string;
  href: string;
  buttonLabel?: string | null;
  tag?: {
    __typename?: "GCMS_ProductReviewTag";
    name: string;
    color: Gcms_ButtonTheme;
  } | null;
};

export type GcmsProductReviewGridFragment = {
  __typename?: "GCMS_ProductReviewGrid";
  id: string;
  title: string;
  description?: { __typename?: "GCMS_RichText"; raw: any } | null;
  disclaimer?: { __typename?: "GCMS_RichText"; raw: any } | null;
  products: Array<{
    __typename?: "GCMS_ProductReviewCard";
    id: string;
    name: string;
    priceRating: number;
    imageUrl: string;
    href: string;
    buttonLabel?: string | null;
    tag?: {
      __typename?: "GCMS_ProductReviewTag";
      name: string;
      color: Gcms_ButtonTheme;
    } | null;
  }>;
};

export type GcmsReportCtaFragment = {
  __typename?: "GCMS_ReportCta";
  id: string;
  freeReportButton?: string | null;
  plansButton?: string | null;
  description?: { __typename?: "GCMS_RichText"; raw: any } | null;
};

export type GcmsResourceFragment = {
  __typename?: "GCMS_Resource";
  id: string;
  title?: string | null;
  subtitle?: string | null;
  slug?: string | null;
  content?: { __typename?: "GCMS_RichText"; markdown: string } | null;
  form?: {
    __typename?: "GCMS_Form";
    id: string;
    bg?: Gcms_ThemeColor | null;
    color?: Gcms_ThemeColor | null;
    title: string;
    slug?: string | null;
    successMessage?: { __typename?: "GCMS_RichText"; markdown: string } | null;
    fields: Array<
      | {
          __typename: "GCMS_FormCheckbox";
          name?: string | null;
          label?: string | null;
          required?: boolean | null;
          formName?: string | null;
        }
      | {
          __typename: "GCMS_FormInput";
          name?: string | null;
          type?: Gcms_FormInputType | null;
          label?: string | null;
          placeholder?: string | null;
          required?: boolean | null;
          formName?: string | null;
        }
      | {
          __typename: "GCMS_FormSelect";
          name?: string | null;
          formName?: string | null;
          label?: string | null;
          required?: boolean | null;
          choices: Array<{
            __typename?: "GCMS_FormOption";
            value?: string | null;
            option?: string | null;
          }>;
        }
      | {
          __typename: "GCMS_FormTextarea";
          name?: string | null;
          label?: string | null;
          placeholder?: string | null;
          required?: boolean | null;
          formName?: string | null;
        }
    >;
    submitButton?: {
      __typename?: "GCMS_Button";
      id: string;
      label: string;
      variant: Gcms_ButtonVariants;
      theme?: Gcms_ButtonTheme | null;
      buttonAction: Gcms_ButtonAction;
    } | null;
  } | null;
};

export type GcmsResourcePreviewFragment = {
  __typename?: "GCMS_Resource";
  id: string;
  slug?: string | null;
  title?: string | null;
  subtitle?: string | null;
  bg: Gcms_ThemeColor;
  thumbnail?: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
};

export type GcmsResourcesSectionFragment = {
  __typename: "GCMS_ResourcesSection";
  id: string;
  title?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  content?: { __typename?: "GCMS_RichText"; markdown: string } | null;
  resources: Array<{
    __typename?: "GCMS_Resource";
    id: string;
    slug?: string | null;
    title?: string | null;
    subtitle?: string | null;
    bg: Gcms_ThemeColor;
    thumbnail?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
  }>;
};

export type GcmsSectionFragment = {
  __typename: "GCMS_Section";
  id: string;
  title?: string | null;
  subtitle?: string | null;
  contentPosition: Gcms_ContentPosition;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  textAlignment?: Gcms_ContentPosition | null;
  smallText?: boolean | null;
  titleColor?: Gcms_ThemeColor | null;
  rounded?: boolean | null;
  image?: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
  content?: {
    __typename?: "GCMS_SectionContentRichText";
    markdown: string;
  } | null;
  buttons: Array<{
    __typename?: "GCMS_Button";
    id: string;
    href?: string | null;
    label: string;
    theme?: Gcms_ButtonTheme | null;
    buttonAction: Gcms_ButtonAction;
    variant: Gcms_ButtonVariants;
    linkedPage?: { __typename?: "GCMS_Page"; id: string; slug: string } | null;
  }>;
};

export type GcmsSimplePageHeaderFragment = {
  __typename: "GCMS_SimplePageHeader";
  title?: string | null;
  backgroundColor: Gcms_ThemeColor;
  textColor: Gcms_ThemeColor;
  content?: { __typename?: "GCMS_RichText"; markdown: string } | null;
  buttons: Array<{
    __typename?: "GCMS_Button";
    id: string;
    href?: string | null;
    label: string;
    theme?: Gcms_ButtonTheme | null;
    buttonAction: Gcms_ButtonAction;
    variant: Gcms_ButtonVariants;
    linkedPage?: { __typename?: "GCMS_Page"; id: string; slug: string } | null;
  }>;
};

export type GcmsStatsBlockFragment = {
  __typename: "GCMS_StatsBlock";
  title?: string | null;
  subtitle?: string | null;
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  stats: Array<{
    __typename?: "GCMS_Stat";
    stat?: string | null;
    desc?: string | null;
    citation?: string | null;
  }>;
};

export type GcmsTestimonialFragment = {
  __typename?: "GCMS_Testimonial";
  name: string;
  jobTitle?: string | null;
  content?: { __typename?: "GCMS_RichText"; raw: any } | null;
  picture?: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
};

export type GcmsTestimonialSectionFragment = {
  __typename: "GCMS_TestimonialSection";
  bg: Gcms_ThemeColor;
  color: Gcms_ThemeColor;
  testimonials: Array<{
    __typename?: "GCMS_Testimonial";
    name: string;
    jobTitle?: string | null;
    content?: { __typename?: "GCMS_RichText"; raw: any } | null;
    picture?: {
      __typename?: "GCMS_Asset";
      id: string;
      url: string;
      width?: number | null;
      height?: number | null;
      handle: string;
    } | null;
  }>;
};

export type GcmsSeoFragment = {
  __typename?: "GCMS_Seo";
  description?: string | null;
  title?: string | null;
  keywords: Array<string>;
  image?: {
    __typename?: "GCMS_Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
};
